/* eslint-disable */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "archive_session_token";

const AuthCtx = createContext({
  user: null,
  token: null,
  loading: true,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthCtx);

const authClient = (token) =>
  axios.create({
    baseURL: API,
    timeout: 15000,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const res = await authClient(t).get("/auth/me");
      setUser(res.data);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        // Definitive rejection — purge the dead session. Leave a one-shot
        // breadcrumb so /admin/login can explain WHY (the purge happens
        // before the admin route guard gets a chance to tag the URL).
        try {
          if (window.location.pathname.startsWith("/admin")) {
            sessionStorage.setItem("archive_login_reason", "session_expired");
          }
        } catch { /* noop */ }
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
        setToken(null); setUser(null);
      }
      // Transient failures (network blip, timeout, 429, 5xx) MUST keep the
      // stored session — purging here forced a full re-login loop.
    } finally {
      setLoading(false);
    }
  }, []);

  // CRITICAL: skip /me check if we're inside the OAuth callback fragment;
  // AuthCallback will exchange the session_id first.
  useEffect(() => {
    if (typeof window !== "undefined" &&
        window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe(token);
  }, [fetchMe, token]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = useCallback((returnPath = null) => {
    const redirect = window.location.origin + (returnPath || window.location.pathname + window.location.search);
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const logout = useCallback(async () => {
    try { await authClient(token).post("/auth/logout"); } catch { /* noop */ }
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setToken(null); setUser(null);
  }, [token]);

  // Exposed for AuthCallback after successful exchange
  const completeLogin = useCallback((newToken, newUser) => {
    try { localStorage.setItem(STORAGE_KEY, newToken); } catch { /* noop */ }
    setToken(newToken); setUser(newUser); setLoading(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, logout, completeLogin }}>
      {children}
    </AuthCtx.Provider>
  );
};

export default AuthProvider;
